import { useEffect, useState } from "react";
import { Loader2, Plus, Save, SlidersHorizontal, Trash2, ArrowDown, ArrowUp } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TypeSpecsDialog, SKETCH_OPTIONS } from "@/components/settings/TypeSpecsDialog";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/components";

export const NUM_RE = /^(\d+\.?\d*|\.\d+)$/;
export const NUM_ERR = "Enter a number 0 or greater";
export const validNum = (v) => NUM_RE.test(String(v).trim());

const inputCls = (err) =>
  `h-9 w-full rounded-md border bg-slate-900/70 px-3 text-sm text-slate-100 outline-none transition-colors focus:border-amber-500/70 ${err ? "border-red-500/70" : "border-slate-800"}`;

function AddTypeForm({ onAdded }) {
  const [form, setForm] = useState({ name: "", category: "window", cost: "" });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    setErr(errors);
    if (Object.keys(errors).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/types", { name: form.name.trim(), category: form.category, cost_per_sqft: 0 });
      onAdded(data);
      setForm({ name: "", category: form.category, cost: "" });
      toast.success(`Added ${data.name}`);
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_auto] items-start gap-3 border-t border-slate-800 bg-slate-900/40 px-5 py-4">
      <div>
        <input data-testid="new-type-name-input" placeholder="New type name, e.g. Bay Window" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls(err.name)} />
        {err.name && <p data-testid="new-type-name-error" className="mt-1 text-xs text-red-400">{err.name}</p>}
      </div>
      <select data-testid="new-type-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls()}>
        <option value="window">Window</option>
        <option value="door">Door</option>
      </select>
      <button data-testid="add-type-button" type="submit" disabled={busy}
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-amber-500/40 px-4 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
        <Plus className="h-4 w-4" /> Add type
      </button>
    </form>
  );
}

const sketchLabel = (k) => (SKETCH_OPTIONS.find(([v]) => v === k) || [null, "—"])[1];
const fromType = (t) => ({ ...t });

function TypeRow({ t, idx, err, onChange, onDelete, onSpecs, onMove }) {
  const filled = Object.values(t.specs || {}).filter(Boolean).length;
  return (
    <tr data-testid={`settings-type-row-${idx}`} className="border-b border-slate-800/70 transition-colors hover:bg-slate-900/40">
      <td className="px-5 py-2">
        <input data-testid={`settings-type-name-input-${idx}`} value={t.name} onChange={(e) => onChange({ name: e.target.value })} className={inputCls(err?.name)} />
      </td>
      <td className="px-2 py-2">
        <select data-testid={`settings-type-category-select-${idx}`} value={t.category} onChange={(e) => onChange({ category: e.target.value })} className={inputCls()}>
          <option value="window">Window</option>
          <option value="door">Door</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <select data-testid={`settings-type-prices-from-${idx}`} value={t.line_category || ""} title="Which product lines this type prices from"
          onChange={(e) => { const v = e.target.value; api.put(`/settings/types/${t.id}/line-category`, { line_category: v }).then(() => { onChange({ line_category: v }); toast.success("Saved"); }).catch((x) => toast.error(errorMessage(x))); }}
          className={inputCls()} disabled={!t.id}>
          <option value="">From its name</option>
          {Object.entries(CATEGORY_LABELS).filter(([k]) => k !== "screen").map(([k, l]) => <option key={k} value={k}>{l} lines</option>)}
        </select>
      </td>
      <td className="whitespace-nowrap px-1 py-2">
        <button aria-label="Move up" data-testid={`settings-type-up-${idx}`} onClick={() => onMove(-1)} className="p-1 text-slate-500 hover:text-amber-200"><ArrowUp className="h-3.5 w-3.5" /></button>
        <button aria-label="Move down" data-testid={`settings-type-down-${idx}`} onClick={() => onMove(1)} className="p-1 text-slate-500 hover:text-amber-200"><ArrowDown className="h-3.5 w-3.5" /></button>
      </td>
      <td className="px-2 py-2">
        <button data-testid={`settings-type-specs-button-${idx}`} onClick={onSpecs} title="Default specs"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-800 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-200">
          <SlidersHorizontal className="h-3.5 w-3.5" /> {sketchLabel(t.sketch)}{filled ? ` · ${filled} specs` : ""}
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <button data-testid={`settings-type-delete-button-${idx}`} onClick={onDelete} title="Delete type"
          className="rounded-md p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export function PricingSection({ settings, onSaved }) {
  const [rows, setRows] = useState([]);
  const [mult, setMult] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [specsFor, setSpecsFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    setRows(settings.types.map(fromType));
    setMult(String(settings.multiplier));
  }, [settings.types, settings.multiplier]);

  const save = async () => {
    const errs = {};
    rows.forEach((r) => {
      const e = {};
      if (!r.name.trim()) e.name = "Name is required";
      if (Object.keys(e).length) errs[r.id] = e;
    });
    if (!validNum(mult)) errs.multiplier = NUM_ERR;
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Fix the highlighted fields. Nothing was saved.");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/pricing", {
        multiplier: Number(mult),
        types: rows.map((r) => ({ id: r.id, name: r.name.trim(), category: r.category, cost_per_sqft: 0,
          pricing_mode: "component", item_cost: null })),
      });
      onSaved(data);
      toast.success("Pricing saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const r = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/settings/types/${r.id}`);
      setRows((l) => l.filter((x) => x.id !== r.id));
      toast.success(`Deleted ${r.name}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-800 bg-[#F2F2F7]/60" data-testid="settings-pricing-section">
      <div className="flex flex-wrap items-end justify-between gap-6 border-b border-slate-800 px-5 py-5">
        <div>
          <h2 className="text-base font-bold text-slate-100 md:text-lg">Types & pricing</h2>
          <p className="mt-1 text-sm text-slate-400">Every line is priced from your supplier product lines with the Pricing Calculation Guide (total area + sash + glass per pane + crate + extras). Suggested price = cost × multiplier.</p>
        </div>
        <div className="flex items-start gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Markup multiplier</label>
            <div className="relative w-36">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">×</span>
              <input data-testid="settings-markup-input" inputMode="decimal" value={mult} onChange={(e) => setMult(e.target.value)}
                className={`${inputCls(errors.multiplier)} pl-7 text-right font-mono-num`} />
            </div>
            {errors.multiplier && <p data-testid="settings-markup-error" className="mt-1 text-xs text-red-400">{errors.multiplier}</p>}
          </div>
          <button data-testid="save-pricing-button" onClick={save} disabled={saving}
            className="mt-5 inline-flex h-9 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save pricing
          </button>
        </div>
      </div>
      <table className="w-full" data-testid="settings-types-table">
        <thead>
          <tr className="border-b border-slate-800 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="px-5 py-2.5">Type</th><th className="w-[130px] px-2 py-2.5">Category</th><th className="w-[170px] px-2 py-2.5">Prices from</th><th className="w-[60px]" /><th className="w-[170px] px-2 py-2.5">Sketch & specs</th><th className="w-[56px]" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <TypeRow key={r.id} t={r} idx={i} err={errors[r.id]} onDelete={() => setToDelete(r)} onSpecs={() => setSpecsFor(r)}
              onMove={(d) => {
                if (i + d < 0 || i + d >= rows.length) return;
                const next = [...rows];
                [next[i], next[i + d]] = [next[i + d], next[i]];
                setRows(next);
                api.put("/settings/type-order", { ids: next.map((x) => x.id) }).catch((e) => toast.error(errorMessage(e)));
              }}
              onChange={(p) => setRows((l) => l.map((x) => (x.id === r.id ? { ...x, ...p } : x)))} />
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="px-5 py-8 text-center text-sm text-slate-500">No types yet. Add one below.</p>}
      <AddTypeForm onAdded={(t) => setRows((l) => [...l, fromType(t)])} />
      <TypeSpecsDialog type={specsFor} onClose={() => setSpecsFor(null)} options={settings.company?.glass_options}
        onSaved={(t) => setRows((l) => l.map((x) => (x.id === t.id ? { ...x, sketch: t.sketch, specs: t.specs, glass: t.glass } : x)))} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-slate-800 bg-[#F2F2F7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Saved quotes keep their prices. New lines can no longer use this type.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-type-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-type-confirm" onClick={remove} className="bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
